import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { SessionWarningModal } from '@teivor/shared-ui';

import { apiClient } from '../lib/api';
import { settingsKeys, useMySessionsQuery } from '../features/settings/queries';

/** Show the warning when the current session has this many seconds (or fewer) left. */
const WARN_WITHIN_SECONDS = 120;

/**
 * Watches the current device session's real `expiresAt` (from
 * `GET /api/iam/sessions`) and surfaces {@link SessionWarningModal} as expiry
 * approaches. No fake timers — if the backend reports no expiry we stay silent.
 *
 * - "Extend" → `POST /api/iam/auth/refresh` (issues fresh tokens), then refetch
 *   sessions to pick up the new `expiresAt`.
 * - "Sign out" → `POST /api/iam/auth/logout`, then go to /login.
 */
export function SessionWarningGate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const sessions = useMySessionsQuery();
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [dismissedFor, setDismissedFor] = React.useState<string | null>(null);

  const current = React.useMemo(
    () => sessions.data?.find((s) => s.current) ?? null,
    [sessions.data],
  );
  const expiresAtMs = current?.expiresAt ? Date.parse(current.expiresAt) : NaN;
  const hasExpiry = Number.isFinite(expiresAtMs);
  const remainingSeconds = hasExpiry ? Math.max(0, Math.round((expiresAtMs - nowMs) / 1000)) : 0;

  const withinWindow = hasExpiry && remainingSeconds <= WARN_WITHIN_SECONDS;
  // Re-arm the warning whenever a fresh expiry is issued (a different timestamp).
  const expiryKey = hasExpiry ? String(expiresAtMs) : null;
  const open = withinWindow && dismissedFor !== expiryKey;

  // Coarse poll (15s) only to DETECT when we enter the warning window — not for
  // the visible countdown. SessionWarningModal owns its own 1s countdown while
  // open, so we deliberately avoid a second per-second timer here. 15s is well
  // under the 120s warning window, so the modal still appears in good time.
  React.useEffect(() => {
    if (!hasExpiry) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [hasExpiry]);

  const onExtend = async () => {
    try {
      await apiClient.post('/api/iam/auth/refresh');
      await qc.invalidateQueries({ queryKey: settingsKeys.sessions });
      setDismissedFor(null);
    } catch {
      // Refresh failed → the session is effectively gone; send to login.
      navigate('/login');
    }
  };

  const onSignOut = async () => {
    try {
      await apiClient.post('/api/iam/auth/logout');
    } finally {
      navigate('/login');
    }
  };

  return (
    <SessionWarningModal
      open={open}
      onOpenChange={(next) => {
        if (!next && expiryKey) setDismissedFor(expiryKey);
      }}
      remainingSeconds={remainingSeconds}
      onExtend={onExtend}
      onSignOut={onSignOut}
      title={t('session.expiring_title', 'Your session is expiring soon')}
      description={t('session.expiring_description', 'Stay signed in or sign out now.')}
      extendLabel={t('session.extend', 'Extend session')}
      signOutLabel={t('session.sign_out', 'Sign out')}
    />
  );
}
